// types/routeTypes.js
import PropTypes from 'prop-types';

/**
 * Location coordinates structure
 */
export const LocationPropType = PropTypes.shape({
  type: PropTypes.oneOf(['Point']),
  coordinates: PropTypes.arrayOf(PropTypes.number) // [longitude, latitude]
});

/**
 * Route stop structure
 */
export const RouteStopPropType = PropTypes.shape({
  _id: PropTypes.string,
  siteId: PropTypes.string,
  name: PropTypes.string,
  clientName: PropTypes.string,
  jobName: PropTypes.string,
  siteName: PropTypes.string,
  estimatedHours: PropTypes.number,
  estimatedArrivalTime: PropTypes.number, // seconds since day start
  estimatedDepartureTime: PropTypes.number, // seconds since day start
  estimatedDriveDuration: PropTypes.number, // seconds
  order: PropTypes.number,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  location: LocationPropType,
  address: PropTypes.string,
  isNew: PropTypes.bool,
  // Enriched fields added by processRouteStops
  displayName: PropTypes.string,
  orderNumber: PropTypes.number,
  totalStops: PropTypes.number,
  hasOptimizedTimes: PropTypes.bool,
  isFirstStop: PropTypes.bool,
  isLastStop: PropTypes.bool
});

/**
 * Route structure
 */
export const RoutePropType = PropTypes.shape({
  _id: PropTypes.string.isRequired,
  crewId: PropTypes.string.isRequired,
  date: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  dayOfWeek: PropTypes.string,
  stops: PropTypes.arrayOf(RouteStopPropType),
  totalDriveTime: PropTypes.number, // minutes
  totalServiceTime: PropTypes.number, // minutes
  totalMilesDriven: PropTypes.number,
  isOptimized: PropTypes.bool,
  lastOptimized: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  // Additional optimization fields
  optimizationStatus: PropTypes.string,
  optimizationError: PropTypes.string
});

/**
 * Weekly route structure
 */
export const WeeklyRoutePropType = PropTypes.shape({
  day: PropTypes.string.isRequired, // "Monday", "Tuesday", etc.
  routes: PropTypes.arrayOf(RoutePropType),
  sites: PropTypes.array, // Array of site objects
  date: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ])
});

/**
 * Crew structure
 */
export const CrewPropType = PropTypes.shape({
  _id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  color: PropTypes.string,
  textColor: PropTypes.string,
  isActive: PropTypes.bool,
  maxHoursPerDay: PropTypes.number,
  skills: PropTypes.arrayOf(PropTypes.string),
  homeBase: LocationPropType
});

/**
 * Site/Job site structure
 */
export const SitePropType = PropTypes.shape({
  _id: PropTypes.string.isRequired,
  name: PropTypes.string,
  jobName: PropTypes.string,
  clientName: PropTypes.string,
  address: PropTypes.string,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  location: LocationPropType,
  estimatedHours: PropTypes.number,
  frequency: PropTypes.string,
  preferredDay: PropTypes.string,
  preferredDays: PropTypes.arrayOf(PropTypes.string),
  evenOrOddWeeks: PropTypes.oneOf(['even', 'odd', 'both']),
  assignedCrew: PropTypes.string,
  isActive: PropTypes.bool,
  lastVisited: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ])
});

/**
 * Timeline item structure
 */
export const TimelineItemPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  group: PropTypes.string.isRequired, // crew ID
  title: PropTypes.string.isRequired,
  start_time: PropTypes.number.isRequired, // timestamp
  end_time: PropTypes.number.isRequired, // timestamp
  itemType: PropTypes.oneOf(['stop', 'drive']).isRequired,
  color: PropTypes.string,
  className: PropTypes.string,
  // Stop-specific fields
  stopData: RouteStopPropType,
  orderNumber: PropTypes.number,
  totalStops: PropTypes.number,
  estimatedHours: PropTypes.number,
  address: PropTypes.string,
  isActive: PropTypes.bool,
  // Drive-specific fields
  isYardDrive: PropTypes.bool,
  isYardReturn: PropTypes.bool,
  durationMinutes: PropTypes.number
});

/**
 * Timeline group structure
 */
export const TimelineGroupPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  color: PropTypes.string,
  textColor: PropTypes.string,
  height: PropTypes.number
});

/**
 * Route metrics structure
 */
export const RouteMetricsPropType = PropTypes.shape({
  stopCount: PropTypes.number.isRequired,
  totalHours: PropTypes.number.isRequired,
  serviceHours: PropTypes.number.isRequired,
  driveHours: PropTypes.number.isRequired,
  efficiency: PropTypes.number.isRequired, // percentage
  milesDriven: PropTypes.number.isRequired
});

/**
 * Optimization request structure
 */
export const OptimizationRequestPropType = PropTypes.shape({
  crewId: PropTypes.string.isRequired,
  stops: PropTypes.arrayOf(PropTypes.shape({
    siteId: PropTypes.string,
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
    estimatedHours: PropTypes.number
  })).isRequired,
  startLocation: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired
  }),
  endLocation: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired
  }),
  departureTime: PropTypes.number // seconds since day start
});

/**
 * Filter options for routes
 */
export const RouteFiltersPropType = PropTypes.shape({
  crewId: PropTypes.string,
  date: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  optimizedOnly: PropTypes.bool,
  minStops: PropTypes.number,
  itemType: PropTypes.oneOf(['stop', 'drive']),
  startTime: PropTypes.number,
  endTime: PropTypes.number
});

/**
 * Weekly hours structure
 */
export const WeeklyHoursPropType = PropTypes.shape({
  Monday: PropTypes.shape({
    serviceTime: PropTypes.number,
    driveTime: PropTypes.number
  }),
  Tuesday: PropTypes.shape({
    serviceTime: PropTypes.number,
    driveTime: PropTypes.number
  }),
  Wednesday: PropTypes.shape({
    serviceTime: PropTypes.number,
    driveTime: PropTypes.number
  }),
  Thursday: PropTypes.shape({
    serviceTime: PropTypes.number,
    driveTime: PropTypes.number
  }),
  Friday: PropTypes.shape({
    serviceTime: PropTypes.number,
    driveTime: PropTypes.number
  }),
  Saturday: PropTypes.shape({
    serviceTime: PropTypes.number,
    driveTime: PropTypes.number
  }),
  Sunday: PropTypes.shape({
    serviceTime: PropTypes.number,
    driveTime: PropTypes.number
  }),
  total: PropTypes.shape({
    serviceTime: PropTypes.number,
    driveTime: PropTypes.number
  })
});

/**
 * Validation result structure
 */
export const ValidationResultPropType = PropTypes.shape({
  isValid: PropTypes.bool.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired
});

// Export collections
export const RoutesPropType = PropTypes.arrayOf(RoutePropType);
export const CrewsPropType = PropTypes.arrayOf(CrewPropType);
export const SitesPropType = PropTypes.arrayOf(SitePropType);
export const TimelineItemsPropType = PropTypes.arrayOf(TimelineItemPropType);
export const TimelineGroupsPropType = PropTypes.arrayOf(TimelineGroupPropType);